import { useEffect, useState } from 'react'
import { data as remixData, useNavigate, useSearchParams } from 'react-router'
import { CaucionIGJAseguradoScreen } from '#app/components/cotizacion/screens/caucion-igj/CaucionIGJAseguradoScreen'
import { CaucionIGJConfirmationScreen } from '#app/components/cotizacion/screens/caucion-igj/CaucionIGJConfirmationScreen'
import { CaucionIGJIntroScreen } from '#app/components/cotizacion/screens/caucion-igj/CaucionIGJIntroScreen'
import { CaucionIGJTomadorScreen } from '#app/components/cotizacion/screens/caucion-igj/CaucionIGJTomadorScreen'
import { Icon } from '#app/components/ui/icon'
import { Modal } from '#app/components/ui/modal'
import { ProgressBar } from '#app/components/ui/progress-bar'
import { sendCotizacionCaucionIGJEmail } from '#app/utils/email.server.ts'
import { type Route } from './+types/cotizacion-caucion-igj'

export const meta: Route.MetaFunction = () => [
	{ title: 'Cotización Caución IGJ Director - 100prevalor' },
	{
		name: 'description',
		content:
			'Cotiza tu seguro de Caución IGJ para Directores de Sociedades Comerciales',
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const quoteDataString = formData.get('quoteData')

	if (typeof quoteDataString !== 'string') {
		return remixData(
			{ success: false, error: 'Datos inválidos' },
			{ status: 400 },
		)
	}

	const quoteData = JSON.parse(quoteDataString) as CaucionIGJFormData

	try {
		await sendCotizacionCaucionIGJEmail(quoteData)
		return remixData({ success: true })
	} catch (error) {
		console.error('Error sending email:', error)
		return remixData(
			{ success: false, error: 'Error al enviar el email' },
			{ status: 500 },
		)
	}
}

type CaucionIGJFormData = {
	tomadorNombre: string
	tomadorCuit: string
	tomadorDomicilio: string
	tomadorTelefono: string
	tomadorEmail: string
	aseguradoNombre: string
	aseguradoCuit: string
	aseguradoDomicilio: string
	aseguradoTelefono: string
	aseguradoEmail: string
	sumaAsegurada: string
	vigencia: string
}

export default function CotizacionCaucionIGJ() {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const step = parseInt(searchParams.get('step') || '1', 10)

	const [completedSteps, setCompletedSteps] = useState<Set<number>>(
		new Set([1]),
	)
	const [formData, setFormData] = useState<Partial<CaucionIGJFormData>>({})

	const totalSteps = 4

	useEffect(() => {
		if (!completedSteps.has(step)) {
			const lastCompleted = Math.max(...Array.from(completedSteps))
			setSearchParams({ step: String(lastCompleted) })
		}
	}, [step, completedSteps, setSearchParams])

	const getProgressPercentage = () => {
		if (step <= 1) return 0
		const progressMap: Record<number, number> = {
			2: 33,
			3: 66,
			4: 100,
		}
		return progressMap[step] || 0
	}

	const currentProgress = getProgressPercentage()
	const showProgressBar = step >= 2

	const handleNext = async (stepData?: Partial<CaucionIGJFormData>) => {
		if (stepData) {
			setFormData({ ...formData, ...stepData })
		}

		// Send email when moving to confirmation screen (step 4)
		if (step === 3) {
			const finalData = { ...formData, ...stepData }

			try {
				const formDataToSend = new FormData()
				formDataToSend.append('quoteData', JSON.stringify(finalData))

				const response = await fetch('/cotizacion-caucion-igj', {
					method: 'POST',
					body: formDataToSend,
				})

				if (response.ok) {
					console.log('✅ Email Caución IGJ enviado exitosamente')
				} else {
					console.error('❌ Error al enviar email Caución IGJ')
				}
			} catch (error) {
				console.error('❌ Error al enviar email Caución IGJ:', error)
			}
		}

		if (step < totalSteps) {
			setCompletedSteps((prev) => new Set([...prev, step + 1]))
			setSearchParams({ step: String(step + 1) })
		}
	}

	const handleBack = () => {
		if (step > 1) {
			setSearchParams({ step: String(step - 1) })
		}
	}

	const handleClose = () => {
		void navigate('/seguros')
	}

	const handleRestart = () => {
		setFormData({})
		setCompletedSteps(new Set([1]))
		setSearchParams({ step: '1' })
	}

	const renderStep = () => {
		switch (step) {
			case 1:
				return <CaucionIGJIntroScreen onNext={() => handleNext()} />
			case 2:
				return (
					<CaucionIGJTomadorScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={{
							tomadorNombre: formData.tomadorNombre,
							tomadorCuit: formData.tomadorCuit,
							tomadorDomicilio: formData.tomadorDomicilio,
							tomadorTelefono: formData.tomadorTelefono,
							tomadorEmail: formData.tomadorEmail,
						}}
					/>
				)
			case 3:
				return (
					<CaucionIGJAseguradoScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={{
							aseguradoNombre: formData.aseguradoNombre,
							aseguradoCuit: formData.aseguradoCuit,
							aseguradoDomicilio: formData.aseguradoDomicilio,
							aseguradoTelefono: formData.aseguradoTelefono,
							aseguradoEmail: formData.aseguradoEmail,
							sumaAsegurada: formData.sumaAsegurada,
							vigencia: formData.vigencia,
						}}
					/>
				)
			case 4:
				return (
					<CaucionIGJConfirmationScreen
						onBack={handleRestart}
						onClose={handleClose}
						data={formData as CaucionIGJFormData}
					/>
				)
			default:
				return <CaucionIGJIntroScreen onNext={() => handleNext()} />
		}
	}

	return (
		<>
			<div className="fixed inset-0 z-50 h-screen bg-neutral-50 md:hidden">
				<div className="flex h-full flex-col">
					<div className="flex shrink-0 items-center justify-end p-4">
						<button
							onClick={handleClose}
							className="flex items-center justify-center rounded-full p-2 transition-colors hover:bg-neutral-200"
							aria-label="Cerrar"
						>
							<Icon name="cross-1" className="size-6 text-neutral-900" />
						</button>
					</div>

					<div className="flex min-h-0 flex-1 flex-col gap-6 px-6 pb-8">
						{showProgressBar && (
							<ProgressBar progress={currentProgress} icon="file-text" />
						)}
						{renderStep()}
					</div>
				</div>
			</div>

			<div className="hidden md:block">
				<Modal
					isOpen={true}
					onClose={handleClose}
					showCloseButton={false}
					rounded={true}
					maxWidth={616}
				>
					<div className="relative flex h-[700px] w-[616px] flex-col rounded-3xl bg-white shadow-[0px_32px_48px_-12px_rgba(217,217,217,1)]">
						<div className="flex min-h-0 flex-1 flex-col gap-8 p-8">
							{showProgressBar && (
								<ProgressBar progress={currentProgress} icon="file-text" />
							)}

							<div className="flex min-h-0 flex-1 flex-col">{renderStep()}</div>
						</div>
					</div>
				</Modal>
			</div>
		</>
	)
}
